import { Directive, ElementRef, inject, Input, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

@Directive({
  selector: '[aiopsPreserveImages]',
  standalone: true,
})
export class PreserveImagesDirective {
  private el = inject(ElementRef);
  private sanitizer = inject(DomSanitizer);

  @Input()
  set aiopsPreserveImages(html: string) {
    if (!html) return;

    const sanitizedHtml = this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
    const normalizedHtml = this.moveImageBlocksToTop(sanitizedHtml);

    const container = this.el.nativeElement as HTMLElement;

    if (!container.innerHTML) {
      container.innerHTML = normalizedHtml;
      return;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = normalizedHtml;

    this.syncNodes(container, tempDiv);
  }

  private moveImageBlocksToTop(html: string): string {
    if (!html || !html.includes('<img')) {
      return html;
    }

    const root = document.createElement('div');
    root.innerHTML = html;

    const imageBlocks: Node[] = [];
    const contentBlocks: Node[] = [];

    Array.from(root.childNodes).forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        const text = node.textContent?.trim() ?? '';
        if (text.length > 0) {
          contentBlocks.push(node.cloneNode(true));
        }
        return;
      }

      const element = node as Element;
      if (element.querySelector('img')) {
        imageBlocks.push(node.cloneNode(true));
      } else {
        contentBlocks.push(node.cloneNode(true));
      }
    });

    if (imageBlocks.length === 0) {
      return html;
    }

    const ordered = document.createElement('div');
    imageBlocks.forEach((block) => ordered.appendChild(block));
    contentBlocks.forEach((block) => ordered.appendChild(block));
    return ordered.innerHTML;
  }

  private syncNodes(current: HTMLElement, next: HTMLElement): void {
    const currentNodes = Array.from(current.childNodes);
    const nextNodes = Array.from(next.childNodes);

    while (current.childNodes.length > next.childNodes.length) {
      current.removeChild(current.lastChild!);
    }

    nextNodes.forEach((nextNode, i) => {
      const currentNode = currentNodes[i];

      if (!currentNode) {
        current.appendChild(nextNode.cloneNode(true));
        return;
      }

      if (currentNode.nodeType === Node.TEXT_NODE && nextNode.nodeType === Node.TEXT_NODE) {
        if (currentNode.textContent !== nextNode.textContent) {
          currentNode.textContent = nextNode.textContent;
        }
        return;
      }

      if (currentNode.nodeType === Node.ELEMENT_NODE && nextNode.nodeType === Node.ELEMENT_NODE) {
        const currentEl = currentNode as HTMLElement;
        const nextEl = nextNode as HTMLElement;

        if (currentEl.tagName === 'IMG' && nextEl.tagName === 'IMG') {
          const currentSrc = currentEl.getAttribute('src');
          const nextSrc = nextEl.getAttribute('src');
          if (currentSrc !== nextSrc) {
            currentEl.setAttribute('src', nextSrc!);
          }
          return;
        }

        if (currentEl.tagName === nextEl.tagName) {
          this.syncNodes(currentEl, nextEl);
          return;
        }
      }

      current.replaceChild(nextNode.cloneNode(true), currentNode);
    });
  }
}
